import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getHeroContent } from '@/services/heroService';
import { useLanguage } from '@/context/LanguageContext';

const Hero: React.FC = () => {
  const { language, tl } = useLanguage();
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);

  const { data: heroContent, isLoading } = useQuery({
    queryKey: ['heroContent', 'home', language],
    queryFn: () => getHeroContent('home'),
  });

  const content = useMemo(() => {
    if (!heroContent) return null;

    let carousel = heroContent.carousel_images || [];
    // Guard if API returns a JSON string
    if (typeof carousel === 'string') {
      try {
        carousel = JSON.parse(carousel);
      } catch {
        carousel = [];
      }
    }

    return {
      background_image: heroContent.background_image || '',
      media_type: heroContent.media_type || 'image',
      video_url: heroContent.video_url || null,
      carousel_images: Array.isArray(carousel) ? carousel.filter(Boolean) : [],
    };
  }, [heroContent]);

  useEffect(() => {
    if (content?.media_type === 'carousel' && content.carousel_images.length > 1) {
      const interval = setInterval(() => {
        setCurrentCarouselIndex((prev) => (prev + 1) % content.carousel_images.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [content?.media_type, content?.carousel_images]);

  const renderBackgroundMedia = () => {
    if (!content) {
      return isLoading ? null : (
        <div className="absolute inset-0 bg-neutral-200" />
      );
    }

    switch (content.media_type) {
      case 'video':
        if (content.video_url) {
          return (
            <video
              className="absolute inset-0 h-full w-full object-cover"
              autoPlay
              muted
              loop
              playsInline
            >
              <source src={content.video_url} type="video/mp4" />
              {content.background_image ? (
                <img
                  src={content.background_image}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : null}
            </video>
          );
        }
        return content.background_image ? (
          <img
            src={content.background_image}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-neutral-200" />
        );

      case 'carousel':
        if (content.carousel_images.length > 0) {
          return (
            <>
              {content.carousel_images.map((image: string, index: number) => (
                <img
                  key={`${image}-${index}`}
                  src={image}
                  alt=""
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
                    index === currentCarouselIndex ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}
            </>
          );
        }
        return content.background_image ? (
          <img
            src={content.background_image}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-neutral-200" />
        );

      case 'image':
      default:
        return content.background_image ? (
          <img
            src={content.background_image}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-neutral-200" />
        );
    }
  };

  const scrollToContent = () => {
    const next = document.getElementById('home-content');
    if (next) {
      next.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    window.scrollTo({ top: window.innerHeight * 0.92, behavior: 'smooth' });
  };

  return (
    <section className="relative h-[100svh] min-h-[560px] w-full bg-white">
      {/* White frame around media — top clears fixed transparent nav */}
      <div className="absolute inset-x-3 top-[72px] bottom-14 md:inset-x-5 md:top-[84px] md:bottom-16 lg:inset-x-6 lg:top-[96px]">
        <div className="relative h-full w-full overflow-hidden bg-neutral-100">
          {renderBackgroundMedia()}
        </div>
      </div>

      <button
        type="button"
        onClick={scrollToContent}
        aria-label={tl('Défiler', 'Scroll down', 'Scorri')}
        className="absolute bottom-4 left-1/2 z-20 -translate-x-1/2 text-black/70 transition-colors hover:text-black md:bottom-5"
      >
        <ChevronDown size={22} strokeWidth={1.25} />
      </button>
    </section>
  );
};

export default Hero;
